import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Breadcrumbs, Dropdown, Label, Separator } from '@heroui/react';
import {
  IconBuildingArch as BuildingArch,
  IconLogout as LogOut,
  IconShield as Shield,
} from '@tabler/icons-react';
import { AppLayout, Navbar, Sidebar } from '@heroui-pro/react';
import { useHasRoles, useLogout, logout as logoutAction } from '@hs/shared';
import { useRouteTitleResolver } from '@/contexts/RouteTitleResolverContext';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import { useSortedApps } from './use-sorted-apps';

function getUserInitial(user: { username?: string; nickname?: string; realname?: string } | null): string {
  if (!user) return 'U';
  const name = user.nickname?.trim() || user.realname?.trim() || user.username?.trim();
  if (!name) return 'U';
  return name.charAt(0);
}

function displayNickname(user: { nickname?: string } | null): string {
  const n = user?.nickname?.trim();
  return n || '昵称未设置';
}

/** 与后端 UserInfo roles 约定一致：spaceType.roleCode（见 2.7.5.1 角色守卫） */
const ROLES_SPACE_ADMIN = ['TENANT.OWNER', 'PLATFORM.OWNER'] as const;

function resolveNavbarTitle(
  resolveRouteTitle: ReturnType<typeof useRouteTitleResolver>,
  pathname: string,
  apps: ReturnType<typeof useSortedApps>,
): string {
  const fromConfig = resolveRouteTitle(pathname);
  if (fromConfig) {
    return fromConfig;
  }

  const seg = pathname.split('/').filter(Boolean)[0];
  if (seg) {
    const appHit = apps.find((a) => a.code === seg);
    if (appHit) {
      return appHit.name;
    }
  }

  return '页面';
}

/**
 * 主区顶部导航（AppLayout.navbar）：菜单开关、面包屑标题、用户菜单。
 * 对应 HeroUI AppLayout 的 navbar 槽位，与 template-finances 的 FinancesNavbar 一致。
 */
export function AppNavbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const resolveRouteTitle = useRouteTitleResolver();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const apps = useSortedApps();
  const canOpenSpaceAdmin = useHasRoles(user, [...ROLES_SPACE_ADMIN]);

  const { logout, isLoggingOut } = useLogout({
    trpc,
    dispatch,
    logout: logoutAction,
    redirectToHome: true,
  });

  const title = useMemo(
    () => resolveNavbarTitle(resolveRouteTitle, pathname, apps),
    [resolveRouteTitle, pathname, apps],
  );

  return (
    <Navbar maxWidth="full">
      <Navbar.Header>
        <AppLayout.MenuToggle />
        <Sidebar.Trigger />
        <Breadcrumbs className="min-w-0">
          <Breadcrumbs.Item className="min-w-0 font-semibold">
            <span className="truncate text-foreground text-lg font-semibold">{title}</span>
          </Breadcrumbs.Item>
        </Breadcrumbs>
        <Navbar.Spacer />
        <Navbar.Content>
          <Dropdown>
            <Dropdown.Trigger
              aria-label="用户菜单"
              className="rounded-full flex size-9 shrink-0 items-center justify-center border-0 bg-transparent p-0 outline-none transition-colors hover:bg-default-100 data-pressed:bg-default-200 focus-visible:ring-2 focus-visible:ring-focus"
            >
              <Avatar className="size-8" variant="soft">
                {user?.avatarUrl ? (
                  <Avatar.Image
                    alt={user?.nickname?.trim() || user?.username || ''}
                    src={user.avatarUrl}
                  />
                ) : null}
                <Avatar.Fallback className="text-xs font-semibold" delayMs={600}>
                  {getUserInitial(user)}
                </Avatar.Fallback>
              </Avatar>
            </Dropdown.Trigger>
            <Dropdown.Popover className="min-w-[220px]" placement="bottom end">
              <div className="px-3 pt-3 pb-1">
                <div className="flex items-center gap-2">
                  <Avatar size="sm" variant="soft">
                    {user?.avatarUrl ? (
                      <Avatar.Image
                        alt={user?.nickname?.trim() || user?.username || ''}
                        src={user.avatarUrl}
                      />
                    ) : null}
                    <Avatar.Fallback className="text-xs font-semibold" delayMs={600}>
                      {getUserInitial(user)}
                    </Avatar.Fallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-col gap-0">
                    <p className="truncate text-sm leading-5 font-medium">
                      {displayNickname(user)}
                    </p>
                    <p className="truncate text-xs leading-none text-muted">
                      用户名：{user?.username != null && user.username !== '' ? user.username : '—'}
                    </p>
                  </div>
                </div>
              </div>
              <Dropdown.Menu>
                {canOpenSpaceAdmin ? (
                  <Dropdown.Item
                    id="space-admin"
                    textValue="空间管理"
                    onAction={() => navigate('/admin')}
                  >
                    <BuildingArch className="text-muted size-4" stroke={1.5} />
                    <Label>空间管理</Label>
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item
                  id="account-settings"
                  textValue="账户设置"
                  onAction={() => navigate('/my')}
                >
                  <Shield className="text-muted size-4" stroke={1.5} />
                  <Label>账户设置</Label>
                </Dropdown.Item>
                <Separator className="my-1" />
                <Dropdown.Item
                  id="sign-out"
                  isDisabled={isLoggingOut}
                  textValue="退出登录"
                  onAction={() => {
                    void logout();
                  }}
                >
                  <LogOut className="text-muted size-4" stroke={1.5} />
                  <Label>退出登录</Label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </Navbar.Content>
      </Navbar.Header>
    </Navbar>
  );
}
